import styled from "@emotion/styled";
import {
    ButtonClickAnalyticsType,
    KmsTypeComponent,
    KmsTypeMediaSchedulingData,
    PublishData,
    StatisticsIndicatorType,
} from "@kaltura/mediaspace-shared-types";
import {
    baseUrl,
    getComponentFromPath,
    stripTags,
    translate,
    translateTime,
    formattedNumber,
    translatePlural,
} from "@kaltura/mediaspace-shared-utils";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ConfigContext, useConfig } from "@kaltura/mediaspace-shared-data-kms-config";
import { Box } from "@kaltura/mediaspace-shared-styled";
import { ErrorBoundary, MediaSchedule } from "@kaltura/mediaspace-shared-ui";
import React, { useContext } from "react";
import PublishIndicator from "@kaltura/mediaspace-shared-entry/publish/publish-indicator/PublishIndicator";
import { Link } from "@kaltura/ds-react-components";

/**
 * component (exposed) props
 */
export interface DetailsProps {
    fetchMetadata: () => DetailsDetailedProps;
    entryId: string;
    publishData?: PublishData;
    mediaTypeName?: string;
}

type SimpleInfo = {
    /**
     * owner name
     */
    createdBy?: string;

    /**
     * creation date / update date, whichever should show
     */
    date: number;

    /**
     * entry views
     */
    views?: number;

    /**
     * encrypted user (owner) id
     */
    userId: string;
};

/**
 * data required for rendering the component,
 * expected to be retrieved by calling fetchMetadata
 */
export type DetailsDetailedProps = {
    /**
     * scheduling data for scheduled media (scheduled meetings, kwebcasts)
     */
    scheduling?: KmsTypeMediaSchedulingData;

    /**
     * for non-scheduled media, alternative data to show
     */
    simpleInfo?: SimpleInfo;

    /**
     * entry views
     */
    statisticsIndicator?: StatisticsIndicatorType;

    /**
     * entry likes
     */
    likes?: number;

    components?: KmsTypeComponent[];
};

const StyledSchedCtnr = styled("div")(({ theme }) => ({
    ...theme.kaltura.typography.body2,
    display: "inline-flex",
    flexWrap: "wrap",
    alignItems: "center",
    width: "100%",
    gap: theme.spacing(1, 0),
}));

const Statistics = styled("div")(({ theme }) => ({
    ...theme.kaltura.typography.body2,
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    width: "100%",
    gap: theme.spacing(1, 0),
}));

const StyledItem = styled(Box)({
    flexShrink: 0,
});

const Separator = styled("div")(({ theme }) => ({
    borderRadius: "50%",
    backgroundColor: theme.kaltura.palette.tone1,
    width: 5,
    height: 5,
    margin: theme.spacing(0, 1),
    flexShrink: 0,
}));

/**
 * search result entry details -
 * for scheduled media, show scheduling.
 * for non-scheduled, shows creation/update time, owner and number of views
 */
export function Details({ fetchMetadata, entryId, publishData, mediaTypeName }: DetailsProps) {
    const sendButtonAnalytics = useButtonAnalytics();
    const context = useConfig();
    const { dateFormats } = context.application;

    const info = fetchMetadata();

    const configContext = useContext(ConfigContext);
    const isKaf = configContext.application?.isKAF;

    const modulesComponentsBlock = info.components?.map((component: KmsTypeComponent, index: number) => {
        const Component = getComponentFromPath(component.component);

        if (!Component) {
            return null;
        }

        return (
            <ErrorBoundary key={`err-${index}`} fallback={<></>}>
                <Component key={`explore-component-${index}`} {...component.props} />
            </ErrorBoundary>
        );
    });

    if (info.simpleInfo) {
        const { createdBy, date, views = 0, userId } = info.simpleInfo;
        const createdAtFormat = dateFormats.shortDate;
        const formattedCreatedAt = translateTime(date, createdAtFormat);

        const onLinkClick = () => {
            sendButtonAnalytics("Explore sidebar - Created by", ButtonClickAnalyticsType.LINK);
        };

        const formatStatistics = () => {
            let result: string;

            if (info.statisticsIndicator && info.statisticsIndicator === StatisticsIndicatorType.PLAYS) {
                result = views === 1 ? translate("%1 Play", [1]) : translate("%1 Plays", [formattedNumber(views)]);
            }
            else {
                result = views === 1 ? translate("%1 View", [1]) : translate("%1 Views", [formattedNumber(views)]);
            }
            return result;
        };

        const createdByLink = baseUrl + "/createdby/" + userId;
        return (
            <Statistics>
                {createdBy && (
                    <>
                        <StyledItem>
                            {translate("By")}
                            &nbsp;
                            {!isKaf && (
                                <Link href={createdByLink} onClick={onLinkClick} size={"small"}>
                                    {stripTags(createdBy)}
                                </Link>
                            )}
                            {isKaf && stripTags(createdBy)}
                        </StyledItem>
                        <Separator />
                    </>
                )}
                <StyledItem>{formattedCreatedAt}</StyledItem>
                {mediaTypeName && (
                    <>
                        <Separator />
                        {mediaTypeName}
                    </>
                )}
                {!isNaN(views) && info.statisticsIndicator && (
                    <>
                        <Separator />
                        <StyledItem>{formatStatistics()}</StyledItem>
                    </>
                )}
                {info.likes !== undefined && !isNaN(info.likes) && (
                    <>
                        <Separator />
                        <StyledItem>{translatePlural("%1 Like", "%1 Likes", [info.likes])}</StyledItem>
                    </>
                )}
                {publishData && (
                    <>
                        <Separator />
                        <PublishIndicator publishData={publishData} entryId={entryId} />
                    </>
                )}
                {modulesComponentsBlock}
            </Statistics>
        );
    }
    return (
        <StyledSchedCtnr>
            <MediaSchedule schedulingData={info.scheduling!} />
            {publishData && <PublishIndicator publishData={publishData} entryId={entryId} />}
            {modulesComponentsBlock}
        </StyledSchedCtnr>
    );
}

export default Details;
